import React from 'react';
import _ from 'lodash';
import Logger from './Logger';
import store from '../store/configureStore';
import { showDialog } from '../actions/dialog';
import { EmailError } from '../models/EmailError';
import {
  GLOBAL_PATH_PREFIX,
  NETWORK_CODE_FORBIDDEN,
  NETWORK_CODE_SERVER_ERROR,
  NETWORK_CODE_UNAUTHORIZED,
  PAGE_LINK_APPROVAL_ERROR,
  SHRUGGIE,
  TABLE_FLIP
} from '../constants';
import { NavigationUtils } from './NavigationUtils';
import Tenant from '../models/Tenant';
import User from '../models/User';
import { logoutForce } from '../actions/logout';
import { isPopinUrl } from '../Routes';

const handleOnErrorLinkClicked = ($event, helpUrl, onLinkClickedCallback) => {
  $event.preventDefault();
  if (onLinkClickedCallback) {
    onLinkClickedCallback(helpUrl);
  }
  NavigationUtils.openTab(helpUrl);
};

/**
 * @callback OnHelpUrlClicked
 * @param url
 */

/**
 * @typedef {Object} ErrorPayload
 * @property customBody
 * @property {OnHelpUrlClicked} onHelpLinkClicked
 * @property {string} helpLink
 */

/**
 * A simple standard error flow.
 *
 * If there's an error, show a dialog with the error message
 * @param {object} newErrorProp
 * @param {object} oldErrorProp
 * @param {string} dialogTitle
 * @param {ErrorPayload} payload
 * @return {boolean} true if handled, false if not
 */
export const handleErrorWithDialog = (
  newErrorProp, oldErrorProp, dialogTitle, payload = {}) => {
  if (newErrorProp && newErrorProp !== oldErrorProp) {
    if (!handleError(newErrorProp, oldErrorProp)) {
      let helpUrl = newErrorProp.helpUrl;
      if (!helpUrl) {
        if (payload) {
          helpUrl = payload.helpLink;
        }
      }

      store.dispatch(showDialog({
        title: newErrorProp.errorTitle || dialogTitle,
        body: (payload && payload.customBody)
          ? payload.customBody(newErrorProp)
          : (
            <div>
              { newErrorProp.errorMessage }
              <br />
              { helpUrl && <span
                className="link"
                onClick={ $event => handleOnErrorLinkClicked(
                  $event,
                  helpUrl,
                  payload.onHelpLinkClicked
                ) }>
                Learn More
              </span> }
            </div>
          ),
        ok: 'Ok'
      }));
    }
    return true;
  } else {
    return false;
  }
};

/**
 * Simple standard error flow for email upload handling.
 *
 * Some successes but with some errors
 *
 * @param {object} newErrorProp
 * @param {object} oldErrorProp
 * @param {string} dialogTitle
 * @param {ErrorPayload} payload
 * @return {boolean} true if handled, false if not
 */
export const handleEmailErrorsWithDialog = (
  newErrorProp, oldErrorProp, dialogTitle, payload = undefined) => {
  if (newErrorProp && newErrorProp.length > 0 && newErrorProp !== oldErrorProp
    && oldErrorProp && oldErrorProp.length === 0) {
    const unhandledErrors = [];
    for (let error of newErrorProp) {
      if (!handleError(error, null)) {
        unhandledErrors.push(error);
      }
    }
    if (unhandledErrors.length !== 0) {
      let helpUrl = newErrorProp.helpUrl;
      if (!helpUrl) {
        if (payload) {
          helpUrl = payload.helpLink;
        }
      }
      const error = (payload && payload.customBody)
        ? payload.customBody(unhandledErrors)
        : (
          <div>
            { unhandledErrors.map(e => {
              return (
                <div key={ EmailError.getMessage(e) }>
                  { EmailError.getMessage(e) }&nbsp;
                  <span className="font-weight-bold">{ EmailError.getEmails(e).join(', ') }</span>
                  <br />
                  <br />
                </div>
              );
            }) }
            { helpUrl && <span
              className="link" onClick={ $event => handleOnErrorLinkClicked(
              $event,
              helpUrl,
              payload.onHelpLinkClicked
            ) }>Learn More</span> }
          </div>
        );

      store.dispatch(showDialog({
        title: dialogTitle,
        body: error,
        ok: 'Ok'
      }));
    }

    return true;
  } else {
    return false;
  }
};

/**
 * You should basically always call this function when you are handling errors
 *
 * This will handle various common cases such as logging the user out on a 401.
 * Do not handle errors in your component render() calls.
 * @param {object} newErrorProp
 * @param {object} oldErrorProp
 * @return {boolean} true if handled by this error handler, false if not
 */
export const handleError = (newErrorProp, oldErrorProp, customOnOkClicked = () => {}) => {
  if (newErrorProp && newErrorProp !== oldErrorProp) {
    let handled = false;
    const error = newErrorProp;
    if (error.code === NETWORK_CODE_UNAUTHORIZED) {
      handled = true;
      handleAuthError(error, customOnOkClicked);
    } else if (error.code === NETWORK_CODE_FORBIDDEN) {
      handled = true;
      handlePermissionError(error, customOnOkClicked);
    } else if (error.code >= NETWORK_CODE_SERVER_ERROR) {
      handled = true;
      handleServerError(error, customOnOkClicked);
    } else {
      Logger.warn('Unhandled client error:', error);
      handled = false;
    }

    return handled;
  } else {
    return false;
  }
};

const generateRandomNumber = (min, max) => {
  const range = max - min + 1;
  return Math.floor(Math.random() * range) + min;
};

const forceLogOut = () => {
  // Broadcast to redux to clear the store
  Logger.warn('Force user logout');
  store.dispatch(logoutForce());
  NavigationUtils.unauthorizedAccess();
};

const handleAuthError = (error, customOnOkClicked) => {
  // User receives a 401 with email - this is a trial expired warning.
  const email = _.get(error, 'data.email') || '';
  if (!!email) {
    NavigationUtils.routeToLegacy(`/dashboard`);
    return;
  }

  // If the user receives a 401 because they have no cookie, but they are trying to get to /r/q/<slug>/answers
  // we redirect them to Angular in the hope that they will be guest authed.

  const pathName = window.location.pathname;
  if (isPopinUrl(pathName)) {
    Logger.warn('401 received when trying to access the Global dashboard.');
    Logger.warn('If they are a guest, they should actually be able to access this page');
    Logger.warn('Send them back to Angular to be authed and try again');
    store.dispatch(logoutForce());
    NavigationUtils.routeToLegacy(pathName.replace(`${GLOBAL_PATH_PREFIX}/`, ''));
  } else {
    Logger.error('401 received, force log out', error);

    store.dispatch(showDialog({
      closeOnEscape: false,
      closeOnClickOutside: false,
      title: 'Not Logged in.',
      body: (
        <React.Fragment>
          You must be logged in to POPin to access this dashboard.
          <br />Click <span className="link" onClick={ forceLogOut }>here</span> to login.
        </React.Fragment>
      ),
      ok: 'Ok',
      onOk: () => {
        customOnOkClicked();
        forceLogOut();
        return true;
      }
    }));
  }

};

const handleServerError = (error, customOnOkClicked) => {
  Logger.error(`${error.code} received, server may be having problems`, error);

  store.dispatch(showDialog({
    closeOnEscape: false,
    closeOnClickOutside: false,
    title: generateRandomNumber(1, 20) % 2 ? SHRUGGIE : TABLE_FLIP,
    body: (
      <React.Fragment>
        POPin is having some issues connecting to our servers. Please wait a little bit
        before refreshing the page and trying again.
      </React.Fragment>
    ),
    ok: 'Ok',
    onOk: () => {
      customOnOkClicked();
      window.location.reload();
    }
  }));
};

const handleLearnMoreClicked = ($event) => {
  $event.preventDefault();
  NavigationUtils.openTab(PAGE_LINK_APPROVAL_ERROR);
};

const handleContactOwnerClicked = ($event, ownerEmail) => {
  $event.preventDefault();
  window.location.href = `mailto:${ownerEmail}`;
};

const handlePermissionError = (error, customOnOkClicked) => {
  Logger.error('403 received, User is forbidden', error);

  const user = _.get(error, 'payload.user');
  let ownerEmail = '';
  if (user) {
    if (User.hasTenant(user)) {
      const owner = Tenant.getOwner(User.getTenant(user));
      if (owner) {
        ownerEmail = User.getEmail(owner);
      }
    }
  }

  let body = null;
  if (user) {
    // If the user is a guest, or the user tenant is not ED enabled
    if (!User.hasTenant(user) || !Tenant.isOrgDashboardEnabled(User.getTenant(user))) {
      const contactEmail = 'help@popinnow.com';
      body = (
        <React.Fragment>
          You do not have authorizaton to access this dashboard.
          Please contact&nbsp;<span
          className="link"
          onClick={ e => handleContactOwnerClicked(e, contactEmail) }>
            { contactEmail }
          </span>
          &nbsp;for more information.
        </React.Fragment>
      )
    }

    // Body not set, fall back to generic message
    if (!body) {
      // If they pass a user extra, this is an initial auth 403 error. kick them out
      body = (
        <React.Fragment>
          Your organization requires approval to access this dashboard.
          { ownerEmail && (
            <React.Fragment>
              &nbsp;Please contact&nbsp;<span
              className="link"
              onClick={ e => handleContactOwnerClicked(e, ownerEmail) }>{ ownerEmail }</span>
              &nbsp;for approval.
            </React.Fragment>
          ) }
          <div className="link" onClick={ handleLearnMoreClicked }>
            Learn more
          </div>
        </React.Fragment>
      );
    }
  } else {
    // Otherwise, its your standard 403 flavor, so show it
    body = (
      <React.Fragment>
        { error.errorMessage || 'You are not allowed to perform that action.' }
      </React.Fragment>
    );
  }

  store.dispatch(showDialog({
    closeOnEscape: false,
    closeOnClickOutside: false,
    title: user ? 'Approval Needed' : 'Forbidden',
    body: body,
    ok: 'Ok',
    onOk: () => {
      customOnOkClicked();
      if (user) {
        NavigationUtils.routeToLegacy('/dashboard');
        return true;
      } else {
        return false;
      }
    }
  }));

};
